import {
  Requirement,
  RequirementComputedStatus,
  RequirementOverview,
  RequirementStatus,
} from '../types';
import { TagVariants } from '../components/Tags';
import {
  convertDateStringToLocalDate,
  convertDateToUTC,
  getLocalTodayMidnight,
} from './date';

export const isUpcoming = (requirement: RequirementOverview): boolean => {
  let today = new Date();
  today = convertDateToUTC(today);
  // Construct a UTC date 90 days from today
  let todayPlusNinety = new Date();
  todayPlusNinety.setDate(todayPlusNinety.getDate() + 90);
  todayPlusNinety = convertDateToUTC(todayPlusNinety);

  if (requirement.due_date) {
    if (requirement.is_virtual || requirement.is_blocked) {
      return false;
    }
    const dueDate = new Date(requirement.due_date);
    return dueDate > today && dueDate < todayPlusNinety;
  }
  return false;
};

export const isAttributeProducer = (requirement: Requirement) => {
  return requirement.produces.length > 0;
};

export const isManagedByMosey = (requirement: RequirementOverview): boolean => {
  if (!requirement.is_managed) {
    return false;
  }

  if (requirement.managed_provider?.name === 'Mosey') {
    return true;
  }

  // For compatibility, any requirement that is managed but has a null
  // provider is actually managed by Mosey. This won't be needed once
  // the state setup flow marks requirements as managed _and_ sets the
  // provider correctly.
  if (!requirement.managed_provider) {
    return true;
  }

  return false;
};

export const isOverdue = (requirement: RequirementOverview): boolean => {
  if (requirement.is_managed || requirement.is_blocked) {
    return false;
  }

  if (requirement.due_date && requirement.status !== RequirementStatus.Done) {
    // Compare local dates because we want to see if the requirement is past due for them
    const localToday = getLocalTodayMidnight();
    const localDueDate = convertDateStringToLocalDate(requirement.due_date);

    // the task is overdue if the due date is in the past
    return localDueDate < localToday;
  }
  return false;
};

export const isDone = (requirement: RequirementOverview): boolean => {
  // Only show a managed recurring requirement as 'done' if it's
  // within two weeks of the due date or the due_date has passed.
  if (requirement.is_managed && requirement.due_date) {
    let today = new Date();
    today = convertDateToUTC(today);

    const dueDate = new Date(requirement.due_date);

    if (dueDate <= today) {
      return true;
    }

    // Construct a UTC date 14 days from today
    let todayPlusFourteen = new Date();
    todayPlusFourteen.setDate(todayPlusFourteen.getDate() + 14);
    todayPlusFourteen = convertDateToUTC(todayPlusFourteen);

    return dueDate >= today && dueDate <= todayPlusFourteen;
  }

  // A one time managed requirement is always considered done unless
  // it's managed by Mosey (in which case we have more data about the status)
  if (requirement.is_managed && requirement.schedule.category === 'one-time') {
    if (isManagedByMosey(requirement)) {
      return requirement.status === RequirementStatus.Done;
    }
    return true;
  }

  return requirement.status === RequirementStatus.Done;
};

export const isSkipped = (requirement: RequirementOverview): boolean => {
  return requirement.status === RequirementStatus.Skipped;
};

export const isTax = (requirement: RequirementOverview): boolean => {
  return requirement.category === 'tax' || requirement.category === 'tax-setup';
};

export const isPayroll = (requirement: RequirementOverview): boolean => {
  return (
    requirement.category === 'payroll' ||
    requirement.category === 'payroll-setup'
  );
};

export const isRegistration = (requirement: RequirementOverview): boolean => {
  return (
    requirement.category === 'registration' ||
    requirement.category === 'registration-maintenance'
  );
};

export const isRegistrationMaintenance = (
  requirement: RequirementOverview
): boolean => {
  return requirement.category === 'registration-maintenance';
};

export const isHR = (requirement: RequirementOverview): boolean => {
  return requirement.category === 'hr';
};

export const isInsurance = (requirement: RequirementOverview): boolean => {
  return (
    requirement.category === 'insurance' ||
    requirement.category === 'insurance-setup'
  );
};

export const isTodo = (requirement: RequirementOverview): boolean => {
  return requirement.status == RequirementStatus.Todo;
};

export const isInProgress = (requirement: RequirementOverview): boolean => {
  return requirement.status == RequirementStatus.InProgress;
};

export const isDeferred = (requirement: RequirementOverview): boolean => {
  return requirement.status === RequirementStatus.Deferred;
};

export const isNew = (requirement: RequirementOverview): boolean => {
  // Construct a UTC date 5 days from today
  let todayMinusFive = new Date();
  todayMinusFive.setDate(todayMinusFive.getDate() - 5);
  todayMinusFive = convertDateToUTC(todayMinusFive);
  return new Date(requirement.created_at) >= todayMinusFive;
};

export const isAssigned = (requirement: Requirement): boolean => {
  return !!requirement.assigned_user;
};

export const isSetupRequirement = (requirement: Requirement): boolean => {
  return requirement.schedule.category === 'one-time';
};

export const isAll = (): boolean => {
  return true;
};

export const statusColorMapping: {
  [key in RequirementComputedStatus]: TagVariants;
} = {
  [RequirementComputedStatus.Todo]: TagVariants.Teal,
  [RequirementComputedStatus.Done]: TagVariants.Lime,
  [RequirementComputedStatus.Deferred]: TagVariants.Yellow,
  [RequirementComputedStatus.Locked]: TagVariants.Sage,
  [RequirementComputedStatus.Overdue]: TagVariants.Red,
  [RequirementComputedStatus.InProgress]: TagVariants.DarkTeal,
  [RequirementComputedStatus.Managed]: TagVariants.Violet,
};

/**
 * Determines if the requirement is the next one that needs attention during
 * state setup.
 */
export const isSetupRequirementNextable = (
  requirement: Requirement
): boolean => {
  return (
    isSetupRequirement(requirement) &&
    !isAssigned(requirement) &&
    !requirement.is_blocked &&
    !isSkipped(requirement) &&
    !isDeferred(requirement) &&
    !isDone(requirement)
  );
};
